import type { Member } from '~/shared/types/member.js'
import { buildMemberSearchText } from '~/shared/utils/memberSearch.js'
import {
  SEARCH_DEFAULT_LIMIT,
  SEARCH_MIN_LENGTH,
  isSearchableQuery,
  matchesSearchQuery,
  tokenizeSearchQuery,
} from '~/shared/utils/searchText.js'

interface CachedSearchText {
  searchText: string
  subkreweId: string | null
}

/**
 * Per-member search blobs, keyed by the member object itself.
 *
 * Store writes replace member objects rather than mutating them, so a stale
 * entry becomes unreachable and is collected. Without this, every keystroke
 * re-normalized five fields across the whole roster (~3k members).
 */
const searchTextCache = new WeakMap<Member, CachedSearchText>()

function memberSearchText(member: Member): string {
  const subkreweId = member.subkrewe?.id ?? null
  const cached = searchTextCache.get(member)
  // SubKrewe references resolve asynchronously after the member lands in the
  // store, so a blob can predate its SubKrewe name — rebuild when it changes.
  if (cached && cached.subkreweId === subkreweId) return cached.searchText

  const searchText = buildMemberSearchText({
    name: member.name,
    displayName: member.displayName,
    email: member.email,
    phone: member.phone,
    subkreweName: member.subkrewe?.name,
  })
  searchTextCache.set(member, { searchText, subkreweId })
  return searchText
}

export interface MemberSearchOptions {
  /** Maximum results returned. */
  limit?: number
  /** Shortest query that runs. Shorter queries return nothing. */
  minLength?: number
  /** How many members to show before anything is typed. */
  emptyLimit?: number
}

/**
 * Picker semantics: nothing until the query is worth running, then a capped
 * list of matches. Use for autocompletes, command palettes, assign dialogs.
 *
 * Pre-filter `members` in a separate `computed` rather than passing a
 * predicate here — that keeps role and eligibility filtering out of the
 * per-keystroke path.
 */
export function searchMembers<T extends Member>(
  members: T[],
  query: string,
  options: MemberSearchOptions = {},
): T[] {
  const {
    limit = SEARCH_DEFAULT_LIMIT,
    minLength = SEARCH_MIN_LENGTH,
    emptyLimit = 0,
  } = options

  if (!isSearchableQuery(query, minLength)) {
    return emptyLimit > 0 ? members.slice(0, emptyLimit) : []
  }

  const tokens = tokenizeSearchQuery(query)
  const results: T[] = []
  for (const member of members) {
    if (!matchesSearchQuery(memberSearchText(member), tokens)) continue
    results.push(member)
    if (results.length >= limit) break
  }
  return results
}

/**
 * Table semantics: an empty query means every row, and matches are uncapped
 * because the table paginates. Use for member/captain tables and lists.
 */
export function filterMembersByQuery<T extends Member>(members: T[], query: string): T[] {
  if (!isSearchableQuery(query, 1)) return members

  const tokens = tokenizeSearchQuery(query)
  return members.filter(member => matchesSearchQuery(memberSearchText(member), tokens))
}
